import React, { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AuthApiClient } from '@/features/auth/api/AuthApiClient';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useApiClient } from '@/hooks/useApiClient';
import { Callout } from '@/components/ui/callout';
import { useTranslation } from '@/hooks/useTranslation';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { validatePassword } from '@/features/auth/utils/validatePassword';

type PasswordChangerProps = {
  className?: string;
};

export const PasswordChanger: React.FC<PasswordChangerProps> = ({ className = '' }) => {
  const { tokenService } = useAuth();
  const { t } = useTranslation();
  const http = useApiClient();

  const authApiClient = useMemo(() => (http ? new AuthApiClient(http) : null), [http]);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordValidation = validatePassword(newPassword);

  const resetFeedback = () => {
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetFeedback();

    if (newPassword !== confirmPassword) {
      setError(t('profile.password.errors.mismatch'));
      return;
    }

    if (!authApiClient) {
      setError(t('profile.password.errors.client'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authApiClient.changePassword({
        email: tokenService?.getUserMail() ?? '',
        currentPassword,
        newPassword,
      });

      if (response.ok) {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setSuccess(t('profile.password.status.success'));
      } else {
        setError(response.message ?? t('profile.password.errors.default'));
      }
    } catch (err) {
      console.error('Password change error:', err);
      setError(t('profile.password.errors.default'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={cn('grid gap-4', className)}>
      <div className="grid gap-2">
        <Label htmlFor="profile-current-password" className="text-sm font-medium">
          {t('profile.password.fields.current.label')}
        </Label>
        <Input
          id="profile-current-password"
          type="password"
          placeholder={t('profile.password.fields.current.placeholder')}
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
          required
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="profile-new-password" className="text-sm font-medium">
          {t('profile.password.fields.new.label')}
        </Label>
        <Input
          id="profile-new-password"
          type="password"
          placeholder={t('profile.password.fields.new.placeholder')}
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          required
        />
        <p className="text-xs text-muted-foreground">{t('auth.password.requirements.hint')}</p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="profile-confirm-password" className="text-sm font-medium">
          {t('profile.password.fields.confirm.label')}
        </Label>
        <Input
          id="profile-confirm-password"
          type="password"
          placeholder={t('profile.password.fields.confirm.placeholder')}
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          required
        />
      </div>

      <Button
        type="submit"
        className="mt-2 w-full"
        disabled={!passwordValidation.valid}
        loading={isSubmitting}
      >
        {isSubmitting
          ? t('profile.password.actions.submitting')
          : t('profile.password.actions.submit')}
      </Button>

      {error && (
        <Callout tone="error" className="w-full">
          {error}
        </Callout>
      )}
      {success && (
        <Callout tone="success" className="w-full">
          {success}
        </Callout>
      )}
    </form>
  );
};

export default PasswordChanger;
